import { BaseClass } from '@components/BaseClass.js';
import { BehaviorSubject, from, of, Observable, take, throwError, defer, finalize } from 'rxjs';
import { tap, map, switchMap, catchError, shareReplay, debounceTime, filter, skip } from 'rxjs/operators';
import DIContainer, { getInstance } from '@components/DependencyInjectionContainer.js';
import { ENV } from '@components/Environment.js';
import { deepClone } from '@components/Utility/index.js';
/**
 * This class provides utilities for sending HTTP requests.
 * It extends the BaseClass.
 */
export class Request extends BaseClass {
    constructor() {
        super(...arguments);
        this.inFlightRequests = new Map();
        this._activeRequestCount = 0;
        this.activeRequestCount$ = new BehaviorSubject(0);
        this.networkCalm$ = this.activeRequestCount$.pipe(skip(1), debounceTime(3000), filter(count => count === 0), map(() => undefined));
    }
    get CacheManager() {
        return DIContainer.getInstance('CacheManager');
    }
    get SessionTimeoutManager() {
        return DIContainer.getInstance('SessionTimeoutManager');
    }
    /**
     * Submits an HTTP request.
     *
     * The caching behavior supports an optional
     * cacheStrategy parameter which can be either:
     *  - "cache-first" (default): Try the cache first and, if missing, fetch from the network.
     *  - "network-first": Try the network first and, on error, fall back to the cache.
     *
     * @param parameters - The parameters for the request.
     * @returns An observable that resolves with the response data.
     */
    submit(parameters) {
        // Prepare the parameters (including cache key generation)
        parameters = this.parseRequestParameters(parameters);
        const initiator = parameters.initiator;
        const initiatorRecord = parameters.initiatorRecord;
        delete parameters.initiator;
        const abortController = new AbortController();
        this.displayLoadingMessage(parameters);
        initiator?.handleProgress(0);
        initiatorRecord?.handleProgress(0);
        const cacheKey = this.cacheKeyGet(parameters);
        if (parameters.cache) {
            const existing$ = this.inFlightRequests.get(cacheKey);
            if (existing$) {
                // Someone else already started this same cache-enabled request:
                return existing$.pipe(tap({
                    next: () => {
                        initiator?.handleProgress(100);
                        initiatorRecord?.handleProgress(100);
                    },
                    error: () => {
                        initiator?.handleProgress(100);
                        initiatorRecord?.handleProgress(100);
                    }
                }));
            }
        }
        // Use "cache-first" as the default strategy if none is provided.
        const strategy = parameters.cacheStrategy || 'cache-first';
        let request$;
        if (parameters.refreshCache) {
            // If refreshing the cache, delete the existing cache entry then fetch from the network.
            request$ = this.CacheManager.deleteCache('request', cacheKey).pipe(switchMap(() => this.performNetworkFetch(parameters, abortController, cacheKey, initiator, initiatorRecord)));
        }
        else if (parameters.cache) {
            // If caching is enabled...
            if (strategy === 'cache-first') {
                // Cache-first: Try the cache; if no data, fetch from network.
                request$ = this.fetchFromCache(parameters, cacheKey, initiator, initiatorRecord).pipe(switchMap(cachedData => {
                    return cachedData !== false
                        ? of(cachedData)
                        : this.performNetworkFetch(parameters, abortController, cacheKey, initiator, initiatorRecord);
                }));
            }
            else if (strategy === 'network-first') {
                // Network-first: Try network; if it fails, fall back to the cache.
                request$ = this.performNetworkFetch(parameters, abortController, cacheKey, initiator, initiatorRecord).pipe(catchError(networkError => {
                    return this.fetchFromCache(parameters, cacheKey, initiator, initiatorRecord).pipe(switchMap(cachedData => {
                        return cachedData !== false
                            ? of(cachedData)
                            : throwError(() => networkError);
                    }));
                }));
            }
            else {
                // Fallback in case of an unrecognized strategy.
                request$ = this.performNetworkFetch(parameters, abortController, cacheKey, initiator, initiatorRecord);
            }
        }
        else {
            // If caching is not enabled, simply fetch from the network.
            request$ = this.performNetworkFetch(parameters, abortController, cacheKey, initiator, initiatorRecord);
        }
        // Return an observable that subscribes to the chosen request$ stream.
        const shared$ = new Observable(observer => {
            this._activeRequestCount++;
            this.activeRequestCount$.next(this._activeRequestCount);
            let submit$ = of(true);
            // delay any non-authenticate requests if the session expired
            const isAuthRequest = parameters.endpoint.toLowerCase().includes('authenticate');
            const skipQueue = parameters.skipSessionQueue === true;
            if (this.SessionTimeoutManager.isExpired() && !isAuthRequest && !skipQueue) {
                console.warn(`[Request] Session expired — queuing request until session restored: ${parameters.endpoint}/${parameters.action || ''}`);
                submit$ = this.SessionTimeoutManager.sessionRestored$;
            }
            submit$.pipe(switchMap(() => request$))
                .pipe(switchMap((data) => {
                initiator?.handleProgress(100);
                initiatorRecord?.handleProgress(100);
                if (data.success === true && data.code === 200 && !!data.payload._error) {
                    // wait for the warning modal to be OK'd by user before proceeding to run the rest of the success handler
                    return this.handleWarning(data.payload._error, parameters, initiator).pipe(map(() => data));
                }
                else {
                    return of(data);
                }
            }))
                .subscribe({
                next: data => {
                    // initiator?.handleProgress(100);
                    // initiatorRecord?.handleProgress(100);
                    if (data.success === true && data.code === 200 && !!data.payload._error && !parameters.nextOnBackgroundJob) {
                        if (!data.payload._error.find((e) => e.errorno != '99')) {
                            // initiated a background job. Don't .next the observer
                            console.log('Background job started, request success handler skipped.');
                            observer.complete();
                        }
                    }
                    observer.next(data);
                },
                error: error => {
                    initiator?.handleProgress(100);
                    initiatorRecord?.handleProgress(100);
                    this.handleError(error, parameters, observer, initiator);
                },
                complete: () => {
                    observer.complete();
                }
            });
            return () => {
                abortController.abort();
                this._activeRequestCount--;
                this.activeRequestCount$.next(this._activeRequestCount);
            };
        }).pipe(take(1), finalize(() => this.inFlightRequests.delete(cacheKey)), shareReplay({ bufferSize: 1, refCount: true }), map((response) => {
            if (parameters.cache) {
                return deepClone(response);
            }
            else {
                return response;
            }
        }));
        if (parameters.cache) {
            this.inFlightRequests.set(cacheKey, shared$);
        }
        return shared$;
    }
    // private timeoutSetup(resp: RequestResponse) {
    // 	console.log('setup the timeout', resp);
    // }
    /**
     * Performs a network fetch and, if caching is enabled,
     * updates the cache with the response.
     *
     * @param parameters - The request parameters.
     * @param abortController - The AbortController to cancel the fetch if needed.
     * @param cacheKey - The cache key for this request.
     * @param initiator - (Optional) The component that initiated this request.
     * @returns An observable of the network response.
     */
    performNetworkFetch(parameters, abortController, cacheKey, initiator, initiatorRecord) {
        return defer(() => from(this.performFetch(parameters, abortController.signal))).pipe(tap(() => {
            initiator?.handleProgress(100);
            initiatorRecord?.handleProgress(100);
        }), switchMap(data => {
            if (parameters.cache) {
                // Try to cache the response; if caching fails, still return the data.
                return this.CacheManager.setCache('request', cacheKey, data).pipe(catchError(() => of(null)), map(() => data));
            }
            return of(data);
        }));
    }
    /**
     * Attempts to retrieve a cached response.
     *
     * @param _parameters - The request parameters.
     * @param cacheKey - The cache key.
     * @param initiator - (Optional) The component that initiated this request.
     * @returns An observable of the cached data, or false if not found.
     */
    fetchFromCache(_parameters, cacheKey, initiator, initiatorRecord) {
        return this.CacheManager.getCache('request', cacheKey, { defaultReturnValue: false }).pipe(tap((cachedData) => {
            if (cachedData !== false) {
                initiator?.handleProgress(100);
                initiatorRecord?.handleProgress(100);
            }
        }));
    }
    cacheKeyGet(parameters) {
        return parameters.cacheKey || '';
    }
    static buildCacheKey(parameters) {
        let key = parameters.endpoint;
        if (parameters.action) {
            key += `?_a=${encodeURIComponent(parameters.action)}`;
        }
        if (parameters.requestData) {
            const qs = Object.keys(parameters.requestData)
                .filter(k => k !== '_a')
                .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(parameters.requestData[k]))
                .join('&');
            if (qs) {
                key += (parameters.action ? '&' : '?') + qs;
            }
        }
        return key;
    }
    clearCache(parameters) {
        parameters.cache = true;
        parameters.method = parameters.method || 'GET';
        parameters = this.parseRequestParameters(parameters);
        const cacheKey = this.cacheKeyGet(parameters);
        return this.CacheManager.deleteCache('request', cacheKey);
    }
    async performFetch(parameters, signal) {
        // Check if NativeHttpHandler is available (registered only on native platforms)
        if (DIContainer.isSingletonRegistered('NativeHttpHandler')) {
            const nativeHandler = DIContainer.getInstance('NativeHttpHandler');
            const response = await nativeHandler.performNativeFetch(parameters, signal);
            return this.processResponse(response, parameters);
        }
        const doFetch = () => fetch(parameters.endpoint, {
            method: parameters.method,
            signal,
            body: parameters.body,
            headers: parameters.headers
        });
        let response;
        try {
            response = await doFetch();
        }
        catch (err) {
            if (err instanceof TypeError && !signal.aborted) {
                console.warn(`[Request] Network error on ${parameters.method} ${parameters.endpoint}, retrying once:`, err.message);
                await new Promise(r => setTimeout(r, 500));
                response = await doFetch();
            }
            else {
                throw err;
            }
        }
        return this.processResponse(response, parameters);
    }
    /**
     * Processes the fetch response.
     * If the response indicates a 401 status, it logs the user out.
     * Otherwise, it dispatches to the proper response processor.
     */
    async processResponse(response, parameters) {
        const status = response.status;
        if (response.headers.get('X-Refresh-Token-Expiry')) {
            const refreshExpiry = parseInt(response.headers.get('X-Refresh-Token-Expiry') || '');
            if (!isNaN(refreshExpiry)) {
                const data = { payload: { expiry: refreshExpiry * 1000 } }; // multiply x1000 as the header value comes back in seconds, and we need to set the expiry in milleseconds
                DIContainer.getInstance('BroadcastManager').send('expiry-updated', data);
            }
        }
        if (status === 401) {
            this.SessionTimeoutManager.handleSessionTimeout();
        }
        if ((parameters.action === 'download' || parameters.apiRoot === ENV('downloadRoot')) && status === 200) {
            return this.processFile(response);
        }
        else if (parameters.stream && response.body) {
            return this.processStream(response);
        }
        else {
            return this.processTextResponse(response, status);
        }
    }
    async processStream(response) {
        const reader = response.body.getReader();
        const chunks = [];
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            chunks.push(new TextDecoder().decode(value));
        }
        const text = chunks.join('');
        const json = this.formatTextToJSON(text);
        return this.parseStreamResults(json);
    }
    async processFile(response) {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'document';
        if (contentDisposition) {
            const matches = contentDisposition.match(/filename="?([^"]+)"?/);
            if (matches && matches[1])
                filename = matches[1];
        }
        const blob = await response.blob();
        return { file: blob, filename };
    }
    /**
     * Adjusts stream JSON to match non-stream formatting.
     */
    parseStreamResults(data) {
        if (data.payload) {
            const resultKeys = [];
            const resultMetaKeys = [];
            Object.keys(data.payload).forEach(key => {
                if (/^result(\d*)$/.test(key)) {
                    resultKeys.push(key);
                }
                else if (/^result(\d*)MetaData$/.test(key)) {
                    resultMetaKeys.push(key);
                }
            });
            if (resultKeys.length > 2) {
                const keys = resultKeys;
                const numKeys = keys.length;
                const errorKey = 'result' + (numKeys - 1).toString();
                const rsKey = 'result' + (numKeys - 2).toString();
                // Rename the result sets based on _recordset.
                data.payload[rsKey].forEach((result) => {
                    const key = keys[+result.seq - 1];
                    data.payload[result._recordset] = data.payload[key];
                    delete data.payload[key];
                    if (resultMetaKeys.length) {
                        const metaKey = key + 'MetaData';
                        const metaRecordSet = result._recordset + 'MetaData';
                        data.payload[metaRecordSet] = data.payload[metaKey];
                        delete data.payload[metaKey];
                    }
                });
                data.payload._recordset = data.payload[rsKey];
                delete data.payload[rsKey];
                if (resultMetaKeys.length) {
                    delete data.payload[rsKey + 'MetaData'];
                }
                if (data.payload[errorKey].length > 0) {
                    data.payload._error = data.payload[errorKey];
                }
                delete data.payload[errorKey];
                if (resultMetaKeys.length) {
                    delete data.payload[errorKey + 'MetaData'];
                }
            }
        }
        return data;
    }
    async processTextResponse(response, status) {
        const text = await response.text().then(text => {
            if (text.trim().length === 0) {
                throw { message: `${status}: Empty response body.`, success: false, code: status };
            }
            return text;
        });
        return this.formatTextToJSON(text);
    }
    formatTextToJSON(text) {
        try {
            const data = JSON.parse(text);
            if (data) {
                const status = data.code || 500;
                if (data.success && status > 199 && status < 300) {
                    return data;
                }
                if (data.payload?._error && data.payload._error.length > 1) {
                    throw {
                        message: `${status}: ${data.payload._error
                            .map((error) => error.errormessage)
                            .join(', ')}`,
                        messages: data.payload._error.map((error) => error.errormessage),
                        success: false,
                        code: status,
                        _error: data.payload._error
                    };
                }
                else if (data.message) {
                    throw { message: `${status}: ${data.message}.`, success: false, code: status, _error: data.payload?._error };
                }
                else {
                    throw { message: `${status}: Unknown Error.`, success: false, code: status, _error: data.payload?._error };
                }
            }
            else {
                throw { message: '500: Failed to JSON.parse response.', success: false, code: 500 };
            }
        }
        catch (error) {
            if (error && error.message) {
                if (error.message === 'Unexpected token \'<\', "<!DOCTYPE "... is not valid JSON') {
                    // IIS rejected the request and returned an HTML-like response.
                    throw { message: `500: ${this.extractErrorSummary(text)}`, success: false, code: 500 };
                }
                throw error;
            }
            else {
                throw { message: '500: Invalid JSON response.', success: false, code: 500 };
            }
        }
    }
    /**
     * Extracts a summary from an IIS HTML error response.
     */
    extractErrorSummary(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const mainError = doc.querySelector('h3')?.textContent?.trim() || '';
        const subError = doc.querySelector('h4')?.textContent?.trim() || '';
        return `${mainError}. ${subError}.`;
    }
    displayLoadingMessage(parameters) {
        if (parameters.loadingMessage) {
            console.log(parameters.endpoint, parameters.loadingMessage);
            // TODO: Implement actual loading message display logic
        }
    }
    handleError(error, parameters, observer, initiator) {
        if (error.name === 'AbortError') {
            console.log('Fetch aborted');
            observer.complete();
            return;
        }
        if (parameters.showErrors) {
            console.error(`[Request] Displaying error to user for ${parameters.method} ${parameters.endpoint}:`, error.message);
            this.showErrors(error, initiator);
        }
        console.log(error.message);
        observer.error(error);
    }
    showErrors(error, initiator) {
        error.displayed = true;
        if (error._error && initiator && typeof initiator.handleErrorResponse === 'function') {
            initiator.handleErrorResponse(error._error);
        }
        if (error.messages) {
            DIContainer.getInstance('Application').alert({ captions: error.messages, modalStyle: 'error' });
        }
        else {
            DIContainer.getInstance('Application').alert({ caption: error.message, modalStyle: 'error' });
        }
    }
    handleWarning(errors, parameters, initiator) {
        if (!parameters.showWarnings) {
            return of(true);
        }
        let style = 'warning';
        if (!errors.find((e) => e.errorno != '99')) {
            style = 'info';
        }
        if (initiator && initiator.handleErrorResponse === 'function') {
            initiator.handleErrorResponse(errors);
        }
        return DIContainer.getInstance('Application').alert({ captions: errors.map((error) => error.errormessage), modalStyle: style });
    }
    parseRequestParameters(parameters) {
        let qMark = '?';
        let qsAction = '';
        let queryString = '';
        let cacheKey = '';
        parameters = {
            showErrors: true,
            showWarnings: true,
            headers: {},
            ...parameters
        };
        if (parameters.stream) {
            parameters.requestData = parameters.requestData ? { ...parameters.requestData } : {};
            parameters.requestData._stream = 'Y';
        }
        if (parameters.metadata) {
            parameters.requestData = parameters.requestData ? { ...parameters.requestData } : {};
            parameters.requestData._metadata = 'Y';
        }
        if (parameters.includeDeviceToken) {
            parameters.requestData = parameters.requestData ? { ...parameters.requestData } : {};
            parameters.requestData.devicetoken = '';
        }
        if (parameters.requestData && Object.keys(parameters.requestData).length > 0) {
            Object.keys(parameters.requestData).forEach((key) => {
                const value = parameters.requestData[key];
                if (value === null) {
                    parameters.requestData[key] = '';
                }
                else if (value === undefined) {
                    delete parameters.requestData[key];
                }
            });
        }
        parameters.apiRoot = parameters.apiRoot ? parameters.apiRoot : ENV('apiRoot');
        let route = parameters.endpoint;
        if (route.startsWith('/')) {
            route = route.slice(1);
        }
        parameters.endpoint = parameters.apiRoot + route;
        if (parameters.endpoint.indexOf('?') > -1) {
            qMark = '';
        }
        // console.log('parseRequestParams', parameters)
        if (qMark && parameters.action) {
            qsAction = `?_a=${encodeURIComponent(parameters.action)}`;
            parameters.endpoint += qsAction;
            qMark = '';
        }
        if (!this.SessionTimeoutManager.getExpiry()) {
            parameters.headers = {
                'X-Request-Refresh-Token-Expiry': 'true',
                ...parameters.headers
            };
        }
        if (ENV('appName')) {
            parameters.headers = {
                'X-Q360-Client': ENV('appName'),
                ...parameters.headers
            };
        }
        if (parameters.body) {
            return parameters;
        }
        parameters.body = parameters.method !== 'GET' ? new FormData() : undefined;
        if (parameters.requestData) {
            queryString = Object.keys(parameters.requestData)
                .filter(key => key !== '_a' || !!qMark)
                .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(parameters.requestData[key]))
                .join('&');
            queryString = (qMark ? qMark : '&') + queryString;
            if (parameters.method === 'GET') {
                parameters.endpoint += queryString;
            }
            else {
                Object.entries(parameters.requestData).forEach(([key, value]) => parameters.body.append(key, value));
                if (qMark && parameters.requestData.action) {
                    parameters.endpoint += `?_a=${encodeURIComponent(parameters.requestData.action)}`;
                }
            }
        }
        cacheKey = route + qsAction + queryString;
        cacheKey = cacheKey.replace(/_stream=[YN]?&?/, '').replace(/([&?])$/, '');
        parameters.cacheKey = cacheKey;
        return parameters;
    }
    // Convenience instance methods.
    GET(parameters) {
        return this.submit({ method: 'GET', ...parameters });
    }
    POST(parameters) {
        return this.submit({ method: 'POST', ...parameters });
    }
    PUT(parameters) {
        return this.submit({ method: 'PUT', ...parameters });
    }
    PATCH(parameters) {
        return this.submit({ method: 'PATCH', ...parameters });
    }
    DELETE(parameters) {
        return this.submit({ method: 'DELETE', ...parameters });
    }
    // Convenience static methods.
    static GET(parameters) {
        return getInstance('Request').GET(parameters);
    }
    static POST(parameters) {
        return getInstance('Request').POST(parameters);
    }
    static DELETE(parameters) {
        return getInstance('Request').submit({ method: 'DELETE', ...parameters });
    }
    static PUT(parameters) {
        return getInstance('Request').submit({ method: 'PUT', ...parameters });
    }
    static PATCH(parameters) {
        return getInstance('Request').submit({ method: 'PATCH', ...parameters });
    }
}
/** The version of the Request class */
Request.VERSION = '$Revision: 130827 $';
